import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import { Badge, Collapse, IconButton, List, Menu, MenuItem, Slider, useTheme } from "@material-ui/core";

import Dashboard from "@material-ui/icons/Dashboard";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import Contacts from "@material-ui/icons/Contacts";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import Help from "@material-ui/icons/Help";
import CodeRoundedIcon from "@material-ui/icons/CodeRounded";
import Schedule from "@material-ui/icons/Schedule";
import Speed from "@material-ui/icons/Speed";

import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import PeopleIcon from "@material-ui/icons/People";
import ListIcon from "@material-ui/icons/ListAlt";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import ForumIcon from "@material-ui/icons/Forum";

import AttachFile from "@material-ui/icons/AttachFile";
import DeviceHubOutlined from "@material-ui/icons/DeviceHubOutlined";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import { socketConnection } from "../services/socket";
import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import usePlans from "../hooks/usePlans";
import { AccountCircle, Brightness3, Brightness4, Brightness6, Brightness7, ExitToApp, FormatListBulletedSharp, LocalAtm, PeopleAltOutlined, Style, VolumeDown, VolumeUp } from "@material-ui/icons";

import ColorModeContext from "../layout/themeContext";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";

const useStyles = makeStyles((theme) => ({
    selected: {
        backgroundColor: theme.mode === "dark" ? "#333" : "#e4e8f4",
        "&:hover": {
            backgroundColor: theme.mode === "dark" ? "#333" : "#e4e8f4",
        },
        borderLeft: theme.mode === "dark" ? "3px solid #00BFFF" : "3px solid #0078ff",
    },
    footerBottom: {
        backgroundColor: theme.mode === "dark" ? "#666666" : "#f3f3f3",
        overflow: "hidden",
        clipPath: "inset(0px round 8px)",
        ...(props) => ({
            width: props.collapsed ? "50px" : "auto",
            marginLeft: props.collapsed ? "-10px" : "0px",
        }),
        display: "flex",
        flexDirection: "column",
        //height: "calc(100vh - 54px)",
    },
    scrollableList: {
        overflowY: "auto",
        overflowX: "hidden",
        "&:hover": {
            "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.grey[500],
            },
        },
        "&::-webkit-scrollbar": {
            width: "8px",
        },
        "&::-webkit-scrollbar-track": {
            background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "transparent",
            borderRadius: "4px",
        },
        //flex: 1, // Permite que a lista scrollable cresça e ocupe o espaço restante
        //minHeight: "calc(100vh - 54px)", // Altura mínima para a lista superior
        height: "calc(100vh - 180px)"
    },
    bottomList: {
        height: "auto", // Altura automática para ajustar ao conteúdo
        flexShrink: 0, // Impede que a lista encolha
    },
    footerTop: (props) => ({
        paddingTop: "8px",
        paddingBottom: "8px",
        paddingRight: props.collapsed ? "11px" : "16px",
        paddingLeft: props.collapsed ? "11px" : "16px",
        gap: "16px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        animation: "$fadeInUp 0.5s ease-out forwards",
    }),
    "@keyframes fadeInUp": {
        from: {
            transform: "translateY(20px)",
            opacity: 0,
        },
        to: {
            transform: "translateY(0)",
            opacity: 1,
        },
    },
    listItemText: {
        fontSize: "1rem",
    },
    nested: {
        paddingLeft: theme.spacing(4),
    },
}));

// Agora onClick é passado para o ListItem, garantindo que o clique feche o menu
function ListItemLink(props) {
    const { icon, primary, to, className, selected, onClick } = props;
    const classes = useStyles();

    const renderLink = React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
    ));

    return (
        <li>
            <ListItem
                button
                dense
                component={renderLink}
                onClick={onClick}
                className={`${className ? className : ""} ${selected ? classes.selected : ""}`}
                selected={selected}
            >
                {icon && <ListItemIcon>{icon}</ListItemIcon>}
                <ListItemText primary={primary} classes={{ primary: classes.listItemText }} />
            </ListItem>
        </li>
    );
}

const reducer = (state, action) => {
    if (action.type === "LOAD_CHATS") {
        const chats = action.payload;
        const newChats = [];
        if (isArray(chats)) {
            chats.forEach((chat) => {
                const chatIndex = state.findIndex((u) => u.id === chat.id);
                if (chatIndex !== -1) {
                    state[chatIndex] = chat;
                } else {
                    newChats.push(chat);
                }
            });
        }
        return [...state, ...newChats];
    }
    if (action.type === "UPDATE_CHATS") {
        const chat = action.payload;
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
            state[chatIndex] = chat;
            return [...state];
        } else {
            return [chat, ...state];
        }
    }
    if (action.type === "DELETE_CHAT") {
        const chatId = action.payload;
        const chatIndex = state.findIndex((u) => u.id === chatId);
        if (chatIndex !== -1) {
            state.splice(chatIndex, 1);
        }
        return [...state];
    }
    if (action.type === "RESET") {
        return [];
    }
    if (action.type === "CHANGE_CHAT") {
        const changedChats = state.map((chat) => {
            if (chat.id === action.payload.chat.id) {
                return action.payload.chat;
            }
            return chat;
        });
        return changedChats;
    }
};

const MainListItems = (props) => {
    const { drawerClose, collapsed, volume, setVolume } = props;
    const classes = useStyles(props);
    const { whatsApps } = useContext(WhatsAppsContext);
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const { user, handleLogout } = useContext(AuthContext);
    const { colorMode } = useContext(ColorModeContext);
    const [connectionWarning, setConnectionWarning] = useState(false);
    const [showCampaigns, setShowCampaigns] = useState(false);
    const [showKanban, setShowKanban] = useState(false);
    const [showOpenAi, setShowOpenAi] = useState(false);
    const [showIntegrations, setShowIntegrations] = useState(false);
    const history = useHistory();
    const [showSchedules, setShowSchedules] = useState(false);
    const [showInternalChat, setShowInternalChat] = useState(false);
    const [showExternalApi, setShowExternalApi] = useState(false);
    const location = useLocation();
    const theme = useTheme();

    const [invisible, setInvisible] = useState(true);
    const [pageNumber, setPageNumber] = useState(1);
    const [searchParam] = useState("");
    const [chats, dispatch] = useReducer(reducer, []);
    const { getPlanCompany } = usePlans();
    const [anchorElAccount, setAnchorElAccount] = useState(null);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam]);

    useEffect(() => {
        async function fetchData() {
            const companyId = user.companyId;
            const planConfigs = await getPlanCompany(undefined, companyId);
            setShowCampaigns(planConfigs.plan.useCampaigns);
            setShowKanban(planConfigs.plan.useKanban);
            setShowOpenAi(planConfigs.plan.useOpenAi);
            setShowIntegrations(planConfigs.plan.useIntegrations);
            setShowSchedules(planConfigs.plan.useSchedules);
            setShowInternalChat(planConfigs.plan.useInternalChat);
            setShowExternalApi(planConfigs.plan.useExternalApi);
        }
        fetchData();
    }, [user.companyId, getPlanCompany]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchChats();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber]);

    useEffect(() => {
        const companyId = localStorage.getItem("companyId");
        const socket = socketConnection({ companyId });
        socket.on(`company-${companyId}-chat`, (data) => {
            if (data.action === "new-message" || data.action === "update") {
                dispatch({ type: "CHANGE_CHAT", payload: data });
            }
        });
        return () => {
            socket.disconnect();
        };
    }, []);

    useEffect(() => {
        let unreadsCount = 0;
        if (chats.length > 0) {
            for (let chat of chats) {
                for (let chatUser of chat.users) {
                    if (chatUser.userId === user.id) {
                        unreadsCount += chatUser.unreads;
                    }
                }
            }
        }
        setInvisible(unreadsCount <= 0);
    }, [chats, user.id]);

    useEffect(() => {
        if (localStorage.getItem("cshow")) {
            setShowCampaigns(true);
        }
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (whatsApps.length > 0) {
                const offlineWhats = whatsApps.filter((whats) => {
                    return (
                        whats.status === "qrcode" ||
                        whats.status === "PAIRING" ||
                        whats.status === "DISCONNECTED" ||
                        whats.status === "TIMEOUT" ||
                        whats.status === "OPENING"
                    );
                });
                setConnectionWarning(offlineWhats.length > 0);
            }
        }, 2000);
        return () => clearTimeout(delayDebounceFn);
    }, [whatsApps]);

    const fetchChats = async () => {
        try {
            const { data } = await api.get("/chats/", {
                params: { searchParam, pageNumber },
            });
            dispatch({ type: "LOAD_CHATS", payload: data.records });
        } catch (err) {
            toastError(err);
        }
    };

    const handleClickLogout = () => {
        handleLogout();
    };

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
        setMenuOpen(true);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setMenuOpen(false);
    };

    const handleOpenUserModal = () => {
        setUserModalOpen(true);
        handleCloseMenu();
    };

    const toggleColorMode = () => {
        colorMode.toggleColorMode();
    }

    // const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    // const [volume, setVolume] = useState(() => {
    //     const saved = localStorage.getItem("volume");
    //     return saved ? parseFloat(saved) : 1;
    // });

    return (
        <div className={classes.footerTop}>
            <UserModal open={userModalOpen} onClose={() => setUserModalOpen(false)}userId={user?.id} />
            <div className={classes.footerBottom}>
                <List className={classes.scrollableList}>
                    <Can
                        role={user.profile}
                        perform={"drawer-admin-items:view"}
                        yes={() => (
                            <>
                                <ListItemLink
                                    to="/"
                                    primary="Dashboard"
                                    icon={<Dashboard />}
                                    selected={location.pathname === "/"}
                                    onClick={drawerClose}
                                />
                                <Divider />
                            </>
                        )}
                    />
                    <Can
                        role={user.profile}
                        perform={"drawer-service-items:view"}
                        no={() => (
                            <>
                                <ListItemLink
                                    to="/tickets"
                                    primary={i18n.t("mainDrawer.listItems.tickets")}
                                    icon={<WhatsAppIcon />}
                                    selected={location.pathname === "/tickets"}
                                    onClick={drawerClose}
                                />
                                <ListItemLink
                                    to="/quick-messages"
                                    primary={i18n.t("mainDrawer.listItems.quickMessages")}
                                    icon={<Speed />}
                                    selected={location.pathname === "/quick-messages"}
                                    onClick={drawerClose}
                                />
                                {showKanban && (
                                    <ListItemLink
                                        to="/kanban"
                                        primary="Kanban"
                                        icon={<Style />}
                                        selected={location.pathname === "/kanban"}
                                        onClick={drawerClose}
                                    />
                                )}
                                <ListItemLink
                                    to="/todolist"
                                    primary={i18n.t("Tarefas")}
                                    icon={<FormatListBulletedSharp />}
                                    selected={location.pathname === "/todolist"}
                                    onClick={drawerClose}
                                />
                                <ListItemLink
                                    to="/contacts"
                                    primary={i18n.t("mainDrawer.listItems.contacts")}
                                    icon={<Contacts />}
                                    selected={location.pathname === "/contacts"}
                                    onClick={drawerClose}
                                />
                                {showSchedules && (
                                    <ListItemLink
                                        to="/schedules"
                                        primary={i18n.t("mainDrawer.listItems.schedules")}
                                        icon={<Schedule />}
                                        selected={location.pathname === "/schedules"}
                                        onClick={drawerClose}
                                    />
                                )}
                                <ListItemLink
                                    to="/tags"
                                    primary={i18n.t("mainDrawer.listItems.tags")}
                                    icon={<LocalOfferIcon />}
                                    selected={location.pathname === "/tags"}
                                    onClick={drawerClose}
                                />
                                {showInternalChat && (
                                    <ListItemLink
                                        to="/chats"
                                        primary={i18n.t("mainDrawer.listItems.chats")}
                                        icon={
                                            <Badge color="secondary" variant="dot" invisible={invisible}>
                                                <ForumIcon />
                                            </Badge>
                                        }
                                        selected={location.pathname === "/chats"}
                                        onClick={drawerClose}
                                    />
                                )}
                                <ListItemLink
                                    to="/helps"
                                    primary={i18n.t("mainDrawer.listItems.helps")}
                                    icon={<Help />}
                                    selected={location.pathname === "/helps"}
                                    onClick={drawerClose}
                                />
                            </>
                        )}
                    />
                    <Can
                        role={user.profile}
                        perform="drawer-admin-items:view"
                        yes={() => (
                            <>
                                <CampaignSubmenu
                                    showCampaigns={showCampaigns}
                                    location={location}
                                    drawerClose={drawerClose}
                                />
                                {user.super && (
                                    <ListItemLink
                                        to="/announcements"
                                        primary={i18n.t("mainDrawer.listItems.annoucements")}
                                        icon={<AnnouncementIcon />}
                                        selected={location.pathname === "/announcements"}
                                        onClick={drawerClose}
                                    />
                                )}
                                {showOpenAi && (
                                    <ListItemLink
                                        to="/prompts"
                                        primary={i18n.t("mainDrawer.listItems.prompts")}
                                        icon={<CodeRoundedIcon />}
                                        selected={location.pathname === "/prompts"}
                                        onClick={drawerClose}
                                    />
                                )}
                                {showIntegrations && (
                                    <ListItemLink
                                        to="/queue-integration"
                                        primary={i18n.t("mainDrawer.listItems.queueIntegration")}
                                        icon={<DeviceHubOutlined />}
                                        selected={location.pathname === "/queue-integration"}
                                        onClick={drawerClose}
                                    />
                                )}
                                <ListItemLink
                                    to="/connections"
                                    primary={i18n.t("mainDrawer.listItems.connections")}
                                    icon={
                                        <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
                                            <SyncAltIcon />
                                        </Badge>
                                    }
                                    selected={location.pathname === "/connections"}
                                    onClick={drawerClose}
                                />
                                <ListItemLink
                                    to="/files"
                                    primary={i18n.t("mainDrawer.listItems.files")}
                                    icon={<AttachFile />}
                                    selected={location.pathname === "/files"}
                                    onClick={drawerClose}
                                />
                                <ListItemLink
                                    to="/queues"
                                    primary={i18n.t("mainDrawer.listItems.queues")}
                                    icon={<AccountTreeOutlinedIcon />}
                                    selected={location.pathname === "/queues"}
                                    onClick={drawerClose}
                                />
                                <ListItemLink
                                    to="/users"
                                    primary={i18n.t("mainDrawer.listItems.users")}
                                    icon={<PeopleAltOutlined />}
                                    selected={location.pathname === "/users"}
                                    onClick={drawerClose}
                                />
                                {showExternalApi && (
                                    <ListItemLink
                                        to="/messages-api"
                                        primary={i18n.t("mainDrawer.listItems.messagesAPI")}
                                        icon={<CodeRoundedIcon />}
                                        selected={location.pathname === "/messages-api"}
                                        onClick={drawerClose}
                                    />
                                )}
                                <ListItemLink
                                    to="/financeiro"
                                    primary={i18n.t("mainDrawer.listItems.financeiro")}
                                    icon={<LocalAtm />}
                                    selected={location.pathname === "/financeiro"}
                                    onClick={drawerClose}
                                />
                                <ListItemLink
                                    to="/settings"
                                    primary={i18n.t("mainDrawer.listItems.settings")}
                                    icon={<SettingsOutlinedIcon />}
                                    selected={location.pathname === "/settings"}
                                    onClick={drawerClose}
                                />
                            </>
                        )}
                    />
                </List>
                <Divider />
                <List className={classes.bottomList}>
                    <ListItem button onClick={(e) => setAnchorElAccount(e.currentTarget)}>
                        <ListItemIcon>
                            <AccountCircle />
                        </ListItemIcon>
                        <ListItemText primary="Minha Conta" classes={{ primary: classes.listItemText }} />
                    </ListItem>

                    <Menu
                        id="account-menu"
                        anchorEl={anchorElAccount}
                        open={Boolean(anchorElAccount)}
                        onClose={() => setAnchorElAccount(null)}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                        }}
                        PaperProps={{
                            style: {
                            maxHeight: 300,
                            width: 220,
                            },
                        }}
                        >
                        <MenuItem 
                            onClick={() => {
                                handleOpenUserModal();
                                drawerClose();
                                setAnchorElAccount(null);
                            }}
                        >
                            <ListItemIcon>
                                <PeopleAltOutlined />
                            </ListItemIcon>
                            <ListItemText primary="Perfil" />
                        </MenuItem>
                        <MenuItem onClick={toggleColorMode}>
                            <ListItemIcon>
                                {theme.mode === 'dark' ? <Brightness3 /> : <Brightness7 />}
                            </ListItemIcon>
                            <ListItemText primary="Modo Escuro" />
                        </MenuItem>
                        {/* NOVO MENU ITEM DEDICADO PARA O VOLUME */}
                        <MenuItem 
                            // Desativa o hover/seleção de um MenuItem comum, pois este é um controle
                            style={{ cursor: 'default' }}
                            onClick={(e) => e.stopPropagation()} // Impede o fechamento do Menu ao clicar no slider
                        >
                            <div style={{ padding: '8px 16px', width: '100%', display: 'flex', flexDirection: 'column' }}>
                                {/* Título do controle de volume */}
                                <ListItemText primary="Volume das Notificações" style={{ marginBottom: '8px' }} />
                                
                                {/* Componente Slider */}
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <VolumeDown style={{ marginRight: '8px', color: theme.palette.text.secondary }} />
                                    <NotificationsVolume 
                                        volume={volume} 
                                        // onChange={(newValue) => {
                                        //     //setVolume(newValue);
                                        //     setVolume(newValue);
                                        //     // A função handleVolumeChange dentro de NotificationsVolume já salva no localStorage, 
                                        //     // mas se quiser a lógica aqui:
                                        //     // localStorage.setItem("volume", newValue);
                                        // }} 
                                        onChange={setVolume}
                                    />
                                    <VolumeUp style={{ marginLeft: '8px', color: theme.palette.text.secondary }} />
                                </div>
                            </div>
                        </MenuItem>
                        {/* <MenuItem onClick={() => {
                            handleClickLogout();
                            setAnchorElAccount(null);
                        }}>
                            <ListItemIcon>
                                <ExitToApp />
                            </ListItemIcon>
                            <ListItemText primary="Sair" />
                        </MenuItem> */}
                    </Menu>
                    
                    <li>
                        <ListItem button dense onClick={handleClickLogout}>
                            <ListItemIcon>
                                <ExitToApp />
                            </ListItemIcon>
                            <ListItemText primary={i18n.t("Sair")} classes={{ primary: classes.listItemText }} />
                        </ListItem>
                    </li>
                </List>
            </div>
        </div>
    );
};

// Submenu de Campanhas com tratamento para evitar fechamento do menu quando clicado
function CampaignSubmenu({ showCampaigns, location, drawerClose }) {
    const [open, setOpen] = useState(false);
    const classes = useStyles();

    const handleToggle = (e) => {
        e.stopPropagation(); // Impede que o clique feche o drawer
        setOpen(!open);
    };

    return (
        showCampaigns && (
            <>
                <Divider />
                <ListItem button onClick={handleToggle}>
                    <ListItemIcon>
                        <EventAvailableIcon />
                    </ListItemIcon>
                    <ListItemText primary={i18n.t("mainDrawer.listItems.campaigns")} />
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItem>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        <ListItemLink
                            to="/campaigns"
                            primary="Listagem"
                            icon={<ListIcon />}
                            selected={location.pathname === "/campaigns"}
                            onClick={drawerClose}
                            className={classes.nested}
                        />
                        <ListItemLink
                            to="/contact-lists"
                            primary="Listas de Contatos"
                            icon={<PeopleIcon />}
                            selected={location.pathname === "/contact-lists"}
                            onClick={drawerClose}
                            className={classes.nested}
                        />
                        <ListItemLink
                            to="/campaigns-config"
                            primary="Configurações"
                            icon={<SettingsOutlinedIcon />}
                            selected={location.pathname === "/campaigns-config"}
                            onClick={drawerClose}
                            className={classes.nested}
                        />
                    </List>
                </Collapse>
            </>
        )
    );
}

export default MainListItems;
